import { Paper, Stack } from '@mui/material'
import { useTranslation } from 'react-i18next'

import CustomTypography from '@/components/dataDisplay/CustomTypography'
import { RestartProgressBar } from '@/features/controller/restart/components/RestartProgressBar'
import { RestartStatusSummary } from '@/features/controller/restart/components/RestartStatusSummary'
import { useControllerRestartState } from '@/features/controller/restart/hooks/useControllerRestartState'
import { useControllerRestartStatusPolling } from '@/features/controller/restart/hooks/useControllerRestartStatusPolling'
import { useRestartProgress } from '@/features/controller/restart/hooks/useRestartProgress'
import { useRestartSummary } from '@/features/controller/restart/hooks/useRestartSummary'

type ResourceRestartStatusPanelProps = {
  controllerId: string
}

/**
 * Live restart-status feedback for the resource-scoped Save & Restart flow. Polls the bound
 * controller's restart status (keyed on controllerId) and reuses the controller-side progress/summary
 * UI so this surface has parity with the controller configuration page.
 */
export function ResourceRestartStatusPanel({ controllerId }: Readonly<ResourceRestartStatusPanelProps>) {
  const { t } = useTranslation()
  const { restartStatus } = useControllerRestartState(controllerId)

  const {
    lastRestart,
    isLoading: isLoadingRestartStatus,
    error: restartStatusError,
  } = useControllerRestartStatusPolling({ controllerId, enabled: true })

  const isRestarting = restartStatus === 'restarting'
  const { progress, statusText } = useRestartProgress(isRestarting, lastRestart)
  const lastRestartSummary = useRestartSummary(restartStatus, lastRestart)

  const shouldShowSummary = !isRestarting || lastRestartSummary !== null

  return (
    <Paper sx={{ p: 3, mt: 2 }}>
      <Stack spacing={2}>
        <CustomTypography color="grey.500" variant="caption">
          {t('controllers.restart_status.last_restart_status')}
        </CustomTypography>
        {shouldShowSummary && (
          <RestartStatusSummary
            error={restartStatusError}
            isLoading={isLoadingRestartStatus}
            summary={lastRestartSummary}
          />
        )}
        {isRestarting && <RestartProgressBar progress={progress} statusText={statusText} />}
      </Stack>
    </Paper>
  )
}
